/**
 * Module Grid Settings Panel
 *
 * Compact section for configuring module grid visualization and
 * module-within-string position suggestion. Mounted in the
 * Vector Layout ticket of EditorPanel.
 */

import { useProjectStore } from '../../stores/projectStore';
import { useUIStore } from '../../stores/uiStore';
import { detectPolarityMarkers, suggestPolarity, detectDominantDirections } from '../../domain/topology/moduleGrid';
import { effectiveClassification } from '../../domain/layout/classify';
import { stringIdentifierOk } from '../../domain/topology/deriveTopology';
import { stripFieldPrefix } from '../../domain/topology/suggest';
import type { ModuleGridConfig } from '../../domain/layout/types';
import './ModuleGridPanel.css';

const DEFAULT_CONFIG: ModuleGridConfig = {
  moduleCount: 28,
  moduleWidth: 1.0,
  moduleHeight: 2.0,
  moduleGap: 0.02,
  rows: 1,
  rowGap: 0.02,
  serpentine: true,
  polarity: 'normal',
  directionOffsetDeg: 0,
  gridOffsetAlongM: 0,
  gridOffsetPerpM: 0,
  gridVisible: true,
  gridOpacity: 0.5,
};

export default function ModuleGridPanel() {
  const siteLayout = useProjectStore((s) => s.data.siteLayout);
  const setModuleGridConfig = useProjectStore((s) => s.setModuleGridConfig);
  const batchRemoveStringOverrides = useProjectStore((s) => s.batchRemoveStringOverrides);
  const selectLayoutEntities = useUIStore((s) => s.selectLayoutEntities);
  const showConfirm = useUIStore((s) => s.showConfirm);

  if (!siteLayout?.transform) return null;

  const config = siteLayout.moduleGridConfig;

  // ── Enable button when no config ──
  if (!config) {
    // Try to detect metadata hints
    const stringEntities = siteLayout.textEntities.filter(
      (e) => effectiveClassification(e)?.level === 'string',
    );
    const moduleCounts = stringEntities
      .map((e) => effectiveClassification(e)?.metadata?.modules)
      .filter((n): n is number => n != null);
    const detectedCount = moduleCounts.length > 0
      ? Math.round(moduleCounts.reduce((a, b) => a + b, 0) / moduleCounts.length)
      : undefined;

    // Detect polarity from PDF
    const detectedPol = detectPolarityMarkers(siteLayout);
    const suggestedPol = detectedPol ? suggestPolarity({ ...siteLayout, detectedPolarity: detectedPol }) : null;

    return (
      <div className="mgp">
        <button
          className="mgp__enable-btn"
          onClick={() => {
            const initial: ModuleGridConfig = {
              ...DEFAULT_CONFIG,
              moduleCount: detectedCount ?? DEFAULT_CONFIG.moduleCount,
              polarity: suggestedPol ?? DEFAULT_CONFIG.polarity,
            };
            setModuleGridConfig(initial);
          }}
        >
          Configure Module Grid
          {detectedCount && <span className="mgp__hint"> ({detectedCount} modules detected)</span>}
        </button>
      </div>
    );
  }

  // ── Helpers ──
  const update = (partial: Partial<ModuleGridConfig>) => {
    setModuleGridConfig({ ...config, ...partial });
  };

  const numInput = (
    field: keyof ModuleGridConfig,
    min: number,
    max: number,
    step: number,
  ) => (
    <input
      type="number"
      className="mgp__input"
      min={min}
      max={max}
      step={step}
      value={config[field] as number}
      onChange={(e) => {
        const v = parseFloat(e.target.value);
        if (!isNaN(v)) update({ [field]: Math.max(min, Math.min(max, v)) });
      }}
    />
  );

  // ── Detect polarity hint ──
  const detectedPol = siteLayout.detectedPolarity ?? detectPolarityMarkers(siteLayout);
  const suggestedPol = detectedPol
    ? suggestPolarity({ ...siteLayout, detectedPolarity: detectedPol })
    : null;

  // ── Detect label directions — ALL clusters, not just the winner ──
  // Minority clusters are usually rotation data bugs on a handful of labels;
  // hiding them behind directions[0] made those cells silently point the
  // wrong way. Chips are clickable: select the cluster to batch-fix it.
  const directions = detectDominantDirections(siteLayout);
  const dominantDir = directions.length > 0 ? directions[0] : null;

  // ── Phantom-grid census (grid quality is LOAD-BEARING under grid-only
  // suggestions): a STRING-level entity whose value is not a plausible
  // identifier is very likely a mis-classified header ("2.DCCB2" stamped a
  // full 36-cell grid at a combiner box) or merge glue — every cell it
  // generates can mis-attribute a fault. ──
  const suspectStringIds = siteLayout.textEntities
    .filter((e) => {
      const cls = effectiveClassification(e);
      if (cls?.level !== 'string') return false;
      if (cls.hierarchy && Object.keys(cls.hierarchy).length > 0) return false;
      const bare = stripFieldPrefix('string', cls.value);
      return !stringIdentifierOk(bare);
    })
    .map((e) => e.id);

  // ── Per-string override census ──
  const overrideIds = Object.keys(siteLayout.stringOverrides ?? {});
  const movedModuleTotal = overrideIds.reduce((n, id) => {
    const mp = siteLayout.stringOverrides?.[id]?.modulePositions;
    return n + (mp ? Object.keys(mp).length : 0);
  }, 0);

  // ── Preview diagram ──
  const { moduleCount, rows, serpentine, polarity } = config;
  const modulesPerRow = Math.ceil(moduleCount / rows);

  // Match map gradient: green(0) → yellow(0.5) → red(1), semi-transparent
  function moduleColorBg(num: number): string {
    const t = moduleCount > 1 ? (num - 1) / (moduleCount - 1) : 0;
    let r: number, g: number, b: number;
    if (t < 0.5) {
      const s = t * 2;
      r = Math.round(34 + (234 - 34) * s);
      g = Math.round(197 + (179 - 197) * s);
      b = Math.round(94 + (8 - 94) * s);
    } else {
      const s = (t - 0.5) * 2;
      r = Math.round(234 + (239 - 234) * s);
      g = Math.round(179 + (68 - 179) * s);
      b = Math.round(8 + (68 - 8) * s);
    }
    return `rgba(${r},${g},${b},0.35)`;
  }

  function moduleColorText(num: number): string {
    const t = moduleCount > 1 ? (num - 1) / (moduleCount - 1) : 0;
    let r: number, g: number, b: number;
    if (t < 0.5) {
      const s = t * 2;
      r = Math.round(34 + (234 - 34) * s);
      g = Math.round(197 + (179 - 197) * s);
      b = Math.round(94 + (8 - 94) * s);
    } else {
      const s = (t - 0.5) * 2;
      r = Math.round(234 + (239 - 234) * s);
      g = Math.round(179 + (68 - 179) * s);
      b = Math.round(8 + (68 - 8) * s);
    }
    return `rgb(${r},${g},${b})`;
  }

  // Build evenly-spaced representative module numbers for color scale
  const SCALE_CELLS = 8;
  const scaleNums: number[] = [];
  if (moduleCount <= SCALE_CELLS) {
    for (let i = 1; i <= moduleCount; i++) scaleNums.push(i);
  } else {
    for (let i = 0; i < SCALE_CELLS; i++) {
      scaleNums.push(Math.round(1 + i * (moduleCount - 1) / (SCALE_CELLS - 1)));
    }
  }

  return (
    <div className="mgp">
      {/* Module count */}
      <div className="mgp__row">
        <span className="mgp__label">Modules</span>
        {numInput('moduleCount', 1, 200, 1)}
        <span className="mgp__unit">per string</span>
      </div>

      {/* Module dimensions */}
      <div className="mgp__row">
        <span className="mgp__label">Size</span>
        {numInput('moduleWidth', 0.01, 10, 0.01)}
        <span className="mgp__separator">&times;</span>
        {numInput('moduleHeight', 0.01, 10, 0.01)}
        <span className="mgp__unit">m</span>
      </div>

      {/* Gap */}
      <div className="mgp__row">
        <span className="mgp__label">Gap</span>
        {numInput('moduleGap', 0, 1, 0.01)}
        <span className="mgp__unit">m</span>
      </div>

      {/* Rows */}
      <div className="mgp__row">
        <span className="mgp__label">Rows</span>
        {numInput('rows', 1, 99, 1)}
        {config.rows > 1 && (
          <>
            <span className="mgp__unit">gap</span>
            {numInput('rowGap', 0, 2, 0.01)}
            <span className="mgp__unit">m</span>
          </>
        )}
      </div>

      {/* Serpentine (only for multi-row) */}
      {config.rows > 1 && (
        <div className="mgp__row">
          <span className="mgp__label" />
          <label className="mgp__check">
            <input
              type="checkbox"
              checked={config.serpentine}
              onChange={(e) => update({ serpentine: e.target.checked })}
            />
            Serpentine (zigzag)
          </label>
        </div>
      )}

      {/* Polarity */}
      <div className="mgp__row">
        <span className="mgp__label">Polarity</span>
        <select
          className="mgp__select"
          value={config.polarity}
          onChange={(e) => update({ polarity: e.target.value as 'normal' | 'reversed' })}
        >
          <option value="normal">Normal</option>
          <option value="reversed">Reversed</option>
        </select>
        {suggestedPol && (
          <span className="mgp__hint">({suggestedPol} detected)</span>
        )}
      </div>

      {/* Direction offset */}
      <div className="mgp__row">
        <span className="mgp__label">Dir offset</span>
        {numInput('directionOffsetDeg', -180, 180, 1)}
        <span className="mgp__unit">&deg;</span>
        <div className="mgp__seg" style={{ marginLeft: 2 }}>
          <button className="mgp__seg-btn" onClick={() => update({ directionOffsetDeg: (config.directionOffsetDeg ?? 0) - 90 })}>-90</button>
          <button className="mgp__seg-btn" onClick={() => update({ directionOffsetDeg: 0 })}>0</button>
          <button className="mgp__seg-btn" onClick={() => update({ directionOffsetDeg: (config.directionOffsetDeg ?? 0) + 90 })}>+90</button>
        </div>
      </div>
      {dominantDir && (
        <div className="mgp__row">
          <span className="mgp__label" />
          <span className="mgp__hint">
            Label direction: {dominantDir.angleDeg}&deg; &rarr; Grid: {((dominantDir.angleDeg + (config.directionOffsetDeg ?? 0)) % 360 + 360) % 360}&deg;
          </span>
        </div>
      )}
      {directions.length > 1 && (
        <div className="mgp__row" style={{ flexWrap: 'wrap' }}>
          <span className="mgp__label">Directions</span>
          {directions.map((d, i) => (
            <button
              key={`${d.angleDeg}-${i}`}
              type="button"
              className={`mgp__dir-chip${i > 0 ? ' mgp__dir-chip--stray' : ''}`}
              title={i === 0
                ? `Dominant label direction (${d.entityCount} strings) — click to select them`
                : `${d.entityCount} strings rotated ${d.angleDeg}° — likely a label data bug; click to select and batch-fix Dir`}
              onClick={() => selectLayoutEntities(d.entityIds)}
            >
              {d.angleDeg}&deg; &times;{d.entityCount}{i > 0 ? ' ⚠' : ''}
            </button>
          ))}
        </div>
      )}

      {/* Grid shift (offset along / perpendicular to string direction) */}
      <div className="mgp__row">
        <span className="mgp__label">Grid shift</span>
        {numInput('gridOffsetAlongM', -50, 50, 0.1)}
        <span className="mgp__unit">along</span>
        {numInput('gridOffsetPerpM', -50, 50, 0.1)}
        <span className="mgp__unit">perp</span>
        {(config.gridOffsetAlongM !== 0 || config.gridOffsetPerpM !== 0) && (
          <button
            className="mgp__seg-btn"
            style={{ marginLeft: 2 }}
            onClick={() => update({ gridOffsetAlongM: 0, gridOffsetPerpM: 0 })}
          >
            0
          </button>
        )}
      </div>

      {/* Grid visibility */}
      <div className="mgp__row">
        <label className="mgp__check">
          <input
            type="checkbox"
            checked={config.gridVisible}
            onChange={(e) => update({ gridVisible: e.target.checked })}
          />
          Show grid
        </label>
        <input
          type="range"
          className="mgp__slider"
          min="0"
          max="100"
          step="5"
          value={Math.round(config.gridOpacity * 100)}
          onChange={(e) => update({ gridOpacity: Number(e.target.value) / 100 })}
        />
      </div>

      {/* Color scale preview */}
      <div className="mgp__preview">
        <div className="mgp__preview-row">
          {scaleNums.map((num) => (
            <span
              key={num}
              className="mgp__preview-cell"
              style={{ background: moduleColorBg(num), color: moduleColorText(num) }}
            >{num}</span>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 3 }}>
          <span className="mgp__preview-legend">
            {moduleCount} modules &middot; {modulesPerRow}&times;{rows}
            {serpentine && rows > 1 ? ' serpentine' : ''}
            {polarity === 'reversed' ? ' reversed' : ''}
          </span>
        </div>
      </div>

      {/* Phantom-grid warning — suspect string labels generating cells */}
      {suspectStringIds.length > 0 && (
        <div className="mgp__row" style={{ flexWrap: 'wrap' }}>
          <span className="mgp__hint" style={{ color: '#fbbf24' }}>
            ⚠ {suspectStringIds.length} suspect string label{suspectStringIds.length > 1 ? 's' : ''} —
            not identifier-shaped; likely phantom grids
          </span>
          <button
            type="button"
            className="mgp__seg-btn"
            title="Select the suspect labels — re-level them (e.g. to DCCB) or fix their text; their cells can mis-attribute faults"
            onClick={() => selectLayoutEntities(suspectStringIds)}
          >
            Select
          </button>
        </div>
      )}

      {/* Per-string override census — see what diverges from the site
          defaults, jump to it, or wipe it. */}
      {overrideIds.length > 0 && (
        <div className="mgp__row" style={{ flexWrap: 'wrap' }}>
          <span className="mgp__hint">
            {overrideIds.length} string{overrideIds.length > 1 ? 's' : ''} overridden
            {movedModuleTotal > 0 ? ` · ${movedModuleTotal} modules moved` : ''}
          </span>
          <button
            type="button"
            className="mgp__seg-btn"
            title="Select every string with a per-string override"
            onClick={() => selectLayoutEntities(overrideIds)}
          >
            Select
          </button>
          <button
            type="button"
            className="mgp__remove-btn"
            style={{ marginLeft: 'auto' }}
            onClick={() => {
              showConfirm({
                title: 'Clear string overrides',
                message: `Remove all ${overrideIds.length} per-string grid overrides on this layout? Module counts, directions, sizes and moved module positions return to the site defaults.`,
                confirmLabel: 'Clear all',
                cancelLabel: 'Cancel',
                variant: 'danger',
                onConfirm: () => batchRemoveStringOverrides(overrideIds),
              });
            }}
          >
            Clear all…
          </button>
        </div>
      )}

      {/* Remove grid */}
      <div className="mgp__row" style={{ justifyContent: 'flex-end', marginTop: 4 }}>
        <button
          className="mgp__remove-btn"
          onClick={() => setModuleGridConfig(undefined)}
        >
          Remove Grid
        </button>
      </div>
    </div>
  );
}
